import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { APP_PAGE } from "constants/v2/appConstants";
import { TYPESENSE_SESSION_STORAGE_KEY } from "constants/v2/typesense";
import { isEmpty, omit, pickBy } from "lodash";

import { getTypesenseClips, getTypesenseStreams } from "./actions";

export const typesenseEntityAdapter = createEntityAdapter({
  selectId: (entity) => entity?.id || entity?.streamId,
});

const setSessionStorageItem = (storageKey, value) => {
  sessionStorage.setItem(`${TYPESENSE_SESSION_STORAGE_KEY}${storageKey}`, value);
};

const deleteSessionStorageItem = (storageKey) => {
  sessionStorage.removeItem(`${TYPESENSE_SESSION_STORAGE_KEY}${storageKey}`);
};

const getSessionStorageItem = (storageKey) => {
  return sessionStorage.getItem(`${TYPESENSE_SESSION_STORAGE_KEY}${storageKey}`);
};

const sessionStorageItemExists = (storageKey) => {
  return !!sessionStorage.getItem(`${TYPESENSE_SESSION_STORAGE_KEY}${storageKey}`);
};

const getSavedFilters = (storageKey) => {
  const savedFilters = getSessionStorageItem(storageKey);
  let parsedFilters = {};

  try {
    parsedFilters = savedFilters ? JSON.parse(savedFilters) : {};
  } catch (error) {
    parsedFilters = {};
  }

  return {
    filters: typeof parsedFilters.filters === "object" ? parsedFilters.filters : {},
    visibleFacetOptionCounts:
      typeof parsedFilters.visibleFacetOptionCounts === "object" ? parsedFilters.visibleFacetOptionCounts : {},
    activePage: typeof parsedFilters.activePage === "number" ? parsedFilters.activePage || 1 : 1,
    search: typeof parsedFilters.search === "string" ? parsedFilters.search : "",
  };
};

const saveFilters = (filters, visibleFacetOptionCounts, activePage = 1, storageKey, search = "") => {
  if (sessionStorageItemExists(`${storageKey}:restore`)) {
    deleteSessionStorageItem(`${storageKey}:restore`);
  }

  setSessionStorageItem(storageKey, JSON.stringify({ filters, visibleFacetOptionCounts, activePage, search }));
};

const cleanSessionStorage = (retainedStorageKeys = []) => {
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);

    if (
      key &&
      key.startsWith(TYPESENSE_SESSION_STORAGE_KEY) &&
      retainedStorageKeys.every((retainedKey) => !key.endsWith(retainedKey))
    ) {
      sessionStorage.removeItem(key);
    }
  }
};

const initialState = typesenseEntityAdapter.getInitialState({
  facets: {},
  selectedFilters: {},
  temporaryFilters: {},
  facetSearch: {},
  totalCount: 0,
  silentReload: false,
  visibleFacetOptionCounts: {},
  activePage: 1,
  search: "",
});

export const typesenseSlice = createSlice({
  name: "typesense",
  initialState,
  reducers: {
    setSelectedFilterValueByFacetKey: (state, action) => {
      const { facetKey, value, page, storageKey, activePage } = action.payload;
      const filterType = page === APP_PAGE.HIGHLIGHT_EDITOR ? "temporaryFilters" : "selectedFilters";

      if (!isEmpty(value)) {
        state[filterType][facetKey] = value;
      } else if (typeof value === "boolean") {
        if (value) {
          state[filterType][facetKey] = value;
        } else {
          state[filterType] = omit(state[filterType], facetKey);
        }
      } else {
        state[filterType] = omit(state[filterType], facetKey);
      }

      if (storageKey && page !== APP_PAGE.HIGHLIGHT_EDITOR) {
        saveFilters(state.selectedFilters, state.visibleFacetOptionCounts, state.activePage, storageKey, state.search);
      }

      if (activePage) state.activePage = activePage;
    },
    setSearch: (state, action) => {
      const { search, storageKey } = action.payload;

      state.search = search;
      if (storageKey) {
        saveFilters(state.selectedFilters, state.visibleFacetOptionCounts, state.activePage, storageKey, search);
      }
    },
    applyTemporaryFilters: (state, action) => {
      const { storageKey } = action.payload;

      state.selectedFilters = { ...state.temporaryFilters };
      saveFilters(state.selectedFilters, state.visibleFacetOptionCounts, state.activePage, storageKey, state.search);
    },
    cancelTemporaryFilters: (state, action) => {
      const { storageKey } = action.payload;

      const saved = getSavedFilters(storageKey);

      state.temporaryFilters = saved.filters || {};
      state.activePage = saved.activePage;
      state.search = saved.search;
    },
    setFacetSearch: (state, action) => {
      const { facetKey, searchText } = action.payload;

      state.facetSearch[facetKey] = searchText;
    },
    setSilentReload: (state, action) => {
      state.silentReload = action.payload;
    },
    setVisibleFacetOptionCounts: (state, action) => {
      const { facetKey, visibleCount, storageKey } = action.payload;

      state.visibleFacetOptionCounts[facetKey] = visibleCount;
      if (storageKey)
        saveFilters(state.selectedFilters, state.visibleFacetOptionCounts, state.activePage, storageKey, state.search);
    },
    setActivePage: (state, action) => {
      const { storageKey, activePage } = action.payload;

      state.activePage = activePage;

      if (storageKey)
        saveFilters(state.selectedFilters, state.visibleFacetOptionCounts, activePage, storageKey, state.search);
    },
    setRestoreFilters: (state, action) => {
      const { storageKey } = action.payload;
      const savedState = getSessionStorageItem(storageKey);

      if (!isEmpty(savedState)) {
        setSessionStorageItem(`${storageKey}:restore`, savedState);
        deleteSessionStorageItem(storageKey);
      }
    },
    resetState: (state, action) => {
      const { page, storageKey, retainFacets = [], previousPage } = action.payload;
      const retainStorageKeys = [];
      const restoreStorageKey = `${storageKey}:restore`;

      if (page === APP_PAGE.EDIT_CLIPS) {
        const clipsKey = storageKey.replace(APP_PAGE.EDIT_CLIPS, APP_PAGE.CLIPS);

        if (sessionStorageItemExists(`${clipsKey}:restore`)) {
          setSessionStorageItem(restoreStorageKey, getSessionStorageItem(`${clipsKey}:restore`));
          deleteSessionStorageItem(`${clipsKey}:restore`);
        }
      } else if (page === APP_PAGE.CLIPS) {
        const editClipsKey = storageKey.replace(APP_PAGE.CLIPS, APP_PAGE.EDIT_CLIPS);

        if (sessionStorageItemExists(`${editClipsKey}:restore`)) {
          setSessionStorageItem(restoreStorageKey, getSessionStorageItem(`${editClipsKey}:restore`));
          deleteSessionStorageItem(`${editClipsKey}:restore`);
        }
      }

      if (
        sessionStorageItemExists(`${APP_PAGE.VIDEOS}:restore`) &&
        (page === APP_PAGE.CLIPS || page === APP_PAGE.EDIT_CLIPS)
      ) {
        retainStorageKeys.push(`${APP_PAGE.VIDEOS}:restore`);
      }

      let restoreStorageItemExists = sessionStorageItemExists(restoreStorageKey);

      if (restoreStorageItemExists) {
        setSessionStorageItem(storageKey, getSessionStorageItem(restoreStorageKey));
        retainStorageKeys.push(storageKey);
      }

      if (page === APP_PAGE.VIDEOS && (previousPage === APP_PAGE.CLIPS || previousPage === APP_PAGE.EDIT_CLIPS)) {
        restoreStorageItemExists = sessionStorageItemExists(storageKey);
        retainStorageKeys.push(storageKey);
      }

      const uniqueRetainStorageKeys = [...new Set(retainStorageKeys)];

      cleanSessionStorage(uniqueRetainStorageKeys);

      if (restoreStorageItemExists) {
        const savedFilters = getSavedFilters(storageKey);
        const newPartialState = {
          selectedFilters: savedFilters.filters,
          visibleFacetOptionCounts: savedFilters.visibleFacetOptionCounts,
          activePage: savedFilters.activePage,
          search: savedFilters.search,
        };

        if (!savedFilters.filters.sortBy) {
          if (page === APP_PAGE.CLIPS || page === APP_PAGE.EDIT_CLIPS) {
            newPartialState.selectedFilters.sortBy = {
              field: "startTime",
              order: -1,
            };
          } else if (page === APP_PAGE.VIDEOS) {
            newPartialState.selectedFilters.sortBy = {
              field: "createdAt",
              order: -1,
            };
          }
        }

        return {
          ...state,
          ...newPartialState,
        };
      } else {
        const retainedFacets = retainFacets.reduce((acc, curr) => {
          acc[curr] = state.facets[curr];

          return acc;
        }, {});

        return { ...initialState, facets: retainedFacets };
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTypesenseStreams.fulfilled, (state, action) => {
      const emptyFacetQueries = Object.entries(action.meta.arg.payload?.facetQuery || {})
        .filter(([_, value]) => isEmpty(value))
        .map(([key]) => key);

      state.facets = action.payload?.data?.facets || {};
      state.totalCount = action.payload?.data?.total || 0;
      state.facetSearch = emptyFacetQueries.length
        ? pickBy(state.facetSearch, (value, key) => !emptyFacetQueries.includes(key))
        : state.facetSearch;
      typesenseEntityAdapter.setAll(state, action.payload?.data?.results || []);
    });
    builder.addCase(getTypesenseClips.fulfilled, (state, action) => {
      const emptyFacetQueries = Object.entries(action.meta.arg.payload?.facetQuery || {})
        .filter(([_, value]) => isEmpty(value))
        .map(([key]) => key);

      state.facets = action.payload?.data?.facets || {};
      state.totalCount = action.payload?.data?.total || 0;
      state.facetSearch = emptyFacetQueries.length
        ? pickBy(state.facetSearch, (value, key) => !emptyFacetQueries.includes(key))
        : state.facetSearch;

      typesenseEntityAdapter.setAll(state, action.payload?.data?.results || []);
    });
  },
});

export const {
  setSelectedFilters,
  setSelectedFilterValueByFacetKey,
  setSearch,
  resetSelectedFilters,
  setFacetSearch,
  setSilentReload,
  setVisibleFacetOptionCounts,
  resetState,
  applyTemporaryFilters,
  cancelTemporaryFilters,
  setActivePage,
  setRestoreFilters,
} = typesenseSlice.actions;

export default typesenseSlice.reducer;
